import {
  Alert,
  Button,
  Descriptions,
  Empty,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  formatDataLockDate,
  formatDataLockDateTime,
  getActionLabel,
  getActorLabel,
  getAttributeDisplayValues,
  getCostGroupLabel,
  getFeatureLabel,
  getFieldLabel,
  getStatusDisplay,
} from "./helpers";
import type {
  DataLockAttribute,
  DataLockCostLine,
  DataLockRecord,
} from "./types";
import styles from "./DataLock.module.scss";

type DataLockDetailModalProps = {
  open: boolean;
  loading: boolean;
  error: string | null;
  dataLock: DataLockRecord | null;
  onClose: () => void;
  onRetry: () => void;
};

type AttributeRow = DataLockAttribute & {
  rowKey: string;
};

type CostLineRow = DataLockCostLine & {
  rowKey: string;
};

type PopulatedValue = NonNullable<
  DataLockAttribute["populatedValues"]
>[number];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getNonEmptyString = (value: unknown) =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const getUniqueStrings = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(
      value.filter(
        (item): item is string =>
          typeof item === "string" && item.length > 0,
      ),
    ),
  );
};

const getNestedValue = (value: unknown, key: string) =>
  isRecord(value) ? value[key] : undefined;

const normalizePopulatedValues = (value: unknown): PopulatedValue[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const normalizedValues: PopulatedValue[] = [];
  const seen = new Set<string>();

  for (const item of value) {
    if (!isRecord(item)) {
      continue;
    }

    const normalized: PopulatedValue = {
      _id: getNonEmptyString(item._id),
      value: getNonEmptyString(item.value),
      label: getNonEmptyString(item.label),
      name: getNonEmptyString(item.name),
      code: getNonEmptyString(item.code),
    };
    const identityParts = [
      normalized._id,
      normalized.value,
      normalized.label,
      normalized.name,
      normalized.code,
    ];
    if (!identityParts.some(Boolean)) {
      continue;
    }

    const identity = JSON.stringify(identityParts);
    if (seen.has(identity)) {
      continue;
    }

    seen.add(identity);
    normalizedValues.push(normalized);
  }

  return normalizedValues;
};

const renderTags = (
  values: string[],
  keyPrefix: string,
  color?: string,
) => {
  if (!values.length) {
    return "-";
  }

  return (
    <Space size={[0, 4]} wrap>
      {values.map((value, index) => (
        <Tag color={color} key={`${keyPrefix}-${value}-${index}`}>
          {value}
        </Tag>
      ))}
    </Space>
  );
};

const attributeColumns: ColumnsType<AttributeRow> = [
  {
    title: "Thuộc tính",
    dataIndex: "field",
    render: (field: string) => getFieldLabel(field),
  },
  {
    title: "Giá trị",
    key: "values",
    render: (_value: unknown, attribute: AttributeRow) =>
      renderTags(
        getAttributeDisplayValues(attribute),
        `attribute-${attribute.rowKey}`,
      ),
  },
];

const costLineColumns: ColumnsType<CostLineRow> = [
  {
    title: "Mã dòng",
    dataIndex: "code",
    render: (value: string) => value || "-",
  },
  {
    title: "Tên dòng",
    dataIndex: "name",
    render: (value: string) => value || "-",
  },
];

const buildAttributeRows = (
  attributes: unknown,
  kind: string,
): AttributeRow[] => {
  if (!Array.isArray(attributes)) {
    return [];
  }

  const attributesByField = new Map<string, DataLockAttribute>();

  for (const item of attributes) {
    if (!isRecord(item)) {
      continue;
    }

    const field = getNonEmptyString(item.field);
    if (!field) {
      continue;
    }

    const values = getUniqueStrings(item.values);
    const populatedValues = normalizePopulatedValues(item.populatedValues);
    const existing = attributesByField.get(field);

    if (existing) {
      existing.values = Array.from(new Set([...existing.values, ...values]));
      existing.populatedValues = normalizePopulatedValues([
        ...(existing.populatedValues ?? []),
        ...populatedValues,
      ]);
      continue;
    }

    attributesByField.set(field, { field, values, populatedValues });
  }

  return Array.from(attributesByField.values()).map((attribute) => ({
    ...attribute,
    rowKey: `${kind}:${attribute.field}`,
  }));
};

const normalizeCostLine = (value: unknown): DataLockCostLine | null => {
  if (!isRecord(value)) {
    return null;
  }

  const line = {
    configId: getNonEmptyString(value.configId) ?? "",
    code: getNonEmptyString(value.code) ?? "",
    name: getNonEmptyString(value.name) ?? "",
  };

  return line.configId || line.code || line.name ? line : null;
};

const hasSameCostLineIdentity = (
  first: DataLockCostLine,
  second: DataLockCostLine,
) => {
  if (first.configId && second.configId) {
    return first.configId === second.configId;
  }

  if (first.code && second.code) {
    return first.code === second.code;
  }

  return Boolean(
    !first.configId &&
      !first.code &&
      !second.configId &&
      !second.code &&
      first.name === second.name,
  );
};

const mergeCostLines = (
  first: DataLockCostLine,
  second: DataLockCostLine,
): DataLockCostLine => ({
  configId: first.configId || second.configId,
  code: first.code || second.code,
  name: first.name || second.name,
});

const buildCostLineRows = (lines: unknown): CostLineRow[] => {
  if (!Array.isArray(lines)) {
    return [];
  }

  const consolidated: DataLockCostLine[] = [];

  for (const item of lines) {
    const line = normalizeCostLine(item);
    if (!line) {
      continue;
    }

    const matchIndex = consolidated.findIndex((existing) =>
      hasSameCostLineIdentity(existing, line),
    );
    if (matchIndex < 0) {
      consolidated.push(line);
      continue;
    }

    consolidated[matchIndex] = mergeCostLines(
      consolidated[matchIndex],
      line,
    );

    for (
      let index = consolidated.length - 1;
      index > matchIndex;
      index -= 1
    ) {
      if (
        index === matchIndex ||
        !hasSameCostLineIdentity(
          consolidated[matchIndex],
          consolidated[index],
        )
      ) {
        continue;
      }

      consolidated[matchIndex] = mergeCostLines(
        consolidated[matchIndex],
        consolidated[index],
      );
      consolidated.splice(index, 1);
    }
  }

  return consolidated.map((line) => {
    const identity = line.configId
      ? `config:${line.configId}`
      : line.code
        ? `code:${line.code}`
        : `name:${line.name}`;

    return { ...line, rowKey: `cost-line:${identity}` };
  });
};

const DataLockDetails = ({ dataLock }: { dataLock: DataLockRecord }) => {
  const status = getStatusDisplay(dataLock);
  const actions = getUniqueStrings(dataLock.actions);
  const restrictedAttributes = getNestedValue(
    dataLock.orderScope,
    "restrictedAttributes",
  );
  const whitelistedAttributes = getNestedValue(
    dataLock.orderScope,
    "whitelistedAttributes",
  );
  const restrictedGroups = getUniqueStrings(
    getNestedValue(dataLock.costSheetScope, "restrictedGroups"),
  );
  const restrictedLines = getNestedValue(
    dataLock.costSheetScope,
    "restrictedLines",
  );

  return (
    <Space direction="vertical" size="large" className={styles.DetailContent}>
      <section>
        <Typography.Title level={5} className={styles.SectionTitle}>
          Thông tin chung
        </Typography.Title>
        <Descriptions
          bordered
          size="small"
          column={{ xs: 1, sm: 2 }}
        >
          <Descriptions.Item label="Tên khóa">
            {dataLock.name || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Loại khóa">
            {getFeatureLabel(dataLock.feature)}
          </Descriptions.Item>
          <Descriptions.Item label="Mô tả">
            {dataLock.description || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Ngày hiệu lực">
            {formatDataLockDate(dataLock.from)}
          </Descriptions.Item>
          <Descriptions.Item label="Ngày kết thúc">
            {formatDataLockDate(dataLock.to)}
          </Descriptions.Item>
          <Descriptions.Item label="Trạng thái">
            <Tag color={status.color}>{status.label}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Hành động">
            {renderTags(actions.map(getActionLabel), "detail-action", "blue")}
          </Descriptions.Item>
        </Descriptions>
      </section>

      {dataLock.feature === "ORDER" && (
        <>
          <section>
            <Typography.Title level={5} className={styles.SectionTitle}>
              Thuộc tính khóa
            </Typography.Title>
            <div className={styles.TableWrapper}>
              <Table<AttributeRow>
                bordered
                size="small"
                rowKey="rowKey"
                columns={attributeColumns}
                dataSource={buildAttributeRows(
                  restrictedAttributes,
                  "restricted",
                )}
                pagination={false}
              />
            </div>
          </section>
          <section>
            <Typography.Title level={5} className={styles.SectionTitle}>
              Thuộc tính loại trừ
            </Typography.Title>
            <div className={styles.TableWrapper}>
              <Table<AttributeRow>
                bordered
                size="small"
                rowKey="rowKey"
                columns={attributeColumns}
                dataSource={buildAttributeRows(
                  whitelistedAttributes,
                  "whitelisted",
                )}
                pagination={false}
              />
            </div>
          </section>
        </>
      )}

      {dataLock.feature === "ORDER_COST_SHEET" && (
        <>
          <section>
            <Typography.Title level={5} className={styles.SectionTitle}>
              Nhóm chi phí bị khóa
            </Typography.Title>
            {renderTags(
              restrictedGroups.map(getCostGroupLabel),
              "cost-group",
            )}
          </section>
          <section>
            <Typography.Title level={5} className={styles.SectionTitle}>
              Dòng chi phí bị khóa
            </Typography.Title>
            <div className={styles.TableWrapper}>
              <Table<CostLineRow>
                bordered
                size="small"
                rowKey="rowKey"
                columns={costLineColumns}
                dataSource={buildCostLineRows(restrictedLines)}
                pagination={false}
              />
            </div>
          </section>
        </>
      )}

      <section>
        <Typography.Title level={5} className={styles.SectionTitle}>
          Thông tin hệ thống
        </Typography.Title>
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Người tạo">
            {getActorLabel(dataLock.createdBy)}
          </Descriptions.Item>
          <Descriptions.Item label="Ngày tạo">
            {formatDataLockDateTime(dataLock.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label="Người cập nhật">
            {getActorLabel(dataLock.updatedBy)}
          </Descriptions.Item>
          <Descriptions.Item label="Ngày cập nhật">
            {formatDataLockDateTime(dataLock.updatedAt)}
          </Descriptions.Item>
        </Descriptions>
      </section>
    </Space>
  );
};

export const DataLockDetailModal = ({
  open,
  loading,
  error,
  dataLock,
  onClose,
  onRetry,
}: DataLockDetailModalProps) => {
  const content = loading ? (
    <div className={styles.ModalState}>
      <Spin />
    </div>
  ) : error ? (
    <div className={`${styles.ModalState} ${styles.ErrorState}`}>
      <Alert type="error" showIcon message={error} />
      <Button onClick={onRetry}>Thử lại</Button>
    </div>
  ) : dataLock ? (
    <DataLockDetails dataLock={dataLock} />
  ) : (
    <div className={styles.ModalState}>
      <Empty />
    </div>
  );

  return (
    <Modal
      centered
      open={open}
      title="Chi tiết khóa dữ liệu"
      width={1100}
      destroyOnHidden
      className={styles.DetailModal}
      footer={<Button onClick={onClose}>Đóng</Button>}
      onCancel={onClose}
    >
      {content}
    </Modal>
  );
};
